import React from 'react';

const SecureMessagesHelpPanelContent = () => (
  <div>
    <h2>Secure messaging workspace</h2>
    <p>
      Keep applicant communication inside this secure inbox whenever PATH messaging is available. It
      keeps the conversation attached to the file and makes it easier to follow requests, responses,
      and attachments in one place.
    </p>

    <h3>Tabs explained</h3>
    <ul>
      <li>
        <strong>Inbox:</strong> Messages sent by the applicant or other staff members. Unread items stay bold
        until you open them.
      </li>
      <li>
        <strong>Sent:</strong> Outgoing messages you or your teammates have delivered to the applicant.
        The status in this tab shows whether the message is only sent, read by the applicant, or
        followed by an applicant reply.
      </li>
      <li>
        <strong>Deleted:</strong> Items removed from your view. Emptying this tab clears your Deleted list only;
        it does not recall messages for the applicant or other staff.
      </li>
    </ul>

    <h3>Common actions</h3>
    <ul>
      <li>
        Use <em>New Message</em> to acknowledge an application, request missing documents, or continue a
        thread without leaving the file.
      </li>
      <li>
        Mark a message as urgent when you need the applicant's immediate attention.
      </li>
      <li>
        Attachments you open here are also copied to the Supporting Documents widget for future reference. Labels remain editable in Supporting Documents if you need to rename them.
      </li>
      <li>
        Use search and filters to locate messages by subject, sender, or status.
      </li>
      <li>
        Use <em>Withdraw sent message</em> when a plain sent message must be removed from the applicant's
        live inbox. Messages with linked forms or attachments need support review before withdrawal.
      </li>
      <li>
        Refresh the inbox after expecting new information or when another team member has replied.
      </li>
    </ul>

    <h3>Financial Overview</h3>
    <p>
      Use Reply or New Message and attach Financial Overview. Choose Pre-fill with PATH data
      or Send blank form. An application is not required. Prefilled forms use the current
      household figures in Participant Details; signed returns update those figures and keep
      the signed PDF as a dated record.
    </p>

    <h3>Client Monthly Attendance Report</h3>
    <p>
      Use Reply or New Message and attach Client Monthly Attendance Report. An application is
      not required. If you choose an intervention from this case, PATH prefills its institution
      and program. The client can complete or correct those details before signing.
    </p>

    <h3>Client consents and declarations</h3>
    <p>
      You can attach information-release consent, funding-source acknowledgement, EI consent,
      Indigenous Declaration and Conflict of Interest Form to a case-wide Reply or New Message
      without selecting an application. You can send them together or with an attendance report
      or Financial Overview.
    </p>

    <h3>Working safely</h3>
    <ul>
      <li>Keep messaging professional - conversations are part of the auditable case history.</li>
      <li>If the message leads to an important decision or missed deadline, capture that outcome in Notes as well.</li>
      <li>PATH displays the participant and case context in the compose window and delivers the message to the applicant account linked to that case.</li>
      <li>Use Deleted &gt; Empty Items only to clear your Deleted list (type <strong>delete</strong> to confirm).</li>
      <li>If attachments fail to open, ask the applicant to resend or notify support for recovery.</li>
    </ul>
  </div>
);

SecureMessagesHelpPanelContent.aiContext = `
You are assisting an ISET staff member using the Secure Messaging widget. Explain the purpose of each tab, how to
compose and manage messages, search/filter the inbox, withdrawal of plain sent messages, and what happens to attachments. Clarify that attachments are adopted into Supporting Documents (labels can be edited there). Explain that Sent status shows applicant state (Sent, Read by applicant, Applicant replied), while Inbox unread/read reflects the current staff viewer's mailbox state. Explain that Deleted/Empty Items clears only the current staff viewer's mailbox list and is not the same as withdrawing a sent message. Explain that PATH derives the recipient from the current case and displays that context read-only in the compose window.

Financial Overview can be attached to Reply or New Message without an application. Staff choose Pre-fill with PATH data or Send blank form. Prefill uses current Participant Details household figures; signed returns update that record while preserving signed history. Client Monthly Attendance Report can also be sent from Reply or New Message without an application. Optional intervention prefill must belong to this case; it does not change the message application scope. The signed report remains linked to the same participant, case and originating message. Information-release consent, funding-source acknowledgement, EI consent, Indigenous Declaration and Conflict of Interest Form may also be sent without an application, separately or together with attendance and Financial Overview. Funding agreements, EFT and decision letters retain their existing scope requirements.

When relevant, frame messaging as part of staff workflow: acknowledge applications, request missing information, follow up with applicants, and keep important outcomes reflected in the case notes as well.
`;

export default SecureMessagesHelpPanelContent;
